import { useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { setVideoNote, type LessonVideo } from '../api/lesson-videos.js';
import { Spinner } from './spinner.js';

/**
 * The athlete's note on one video, and the coach's reply (Athlete Portal
 * canvas row 7, approved 16 October; migration 019).
 *
 * While the coach has not opened the video: a field, saved when the athlete
 * leaves it - no Save button, as the coach's own notes work. After: what was
 * written, and the coach's reply, read only. Both are seen by the parent too.
 */
export function VideoNote({ lessonId, video, coachName }: { lessonId: string; video: LessonVideo; coachName: string }) {
  const getToken = useAccessToken();
  const [text, setText] = useState(video.athlete_note ?? '');
  const [saved, setSaved] = useState(video.athlete_note ?? '');
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const id = `note-${video.id}`;

  async function save() {
    if (text.trim() === saved.trim()) return;
    setSaving(true);
    try {
      const result = await setVideoNote(getToken, lessonId, video.id, text);
      setSaved(result.athlete_note ?? '');
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  if (video.can_change) {
    return (
      <div className="field video-note" data-invalid={failed ? '' : undefined}>
        <label htmlFor={id}>Your note</label>
        <input
          id={id}
          type="text"
          maxLength={500}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onBlur={() => void save()}
        />
        {saving ? (
          <span className="hint waiting" role="status">
            <Spinner />
            Saving…
          </span>
        ) : null}
      </div>
    );
  }

  if (!video.athlete_note && !video.coach_reply) return null;
  return (
    <div className="video-note">
      {video.athlete_note ? (
        <div className="note-read">
          <span className="from">You</span>
          <p>{video.athlete_note}</p>
        </div>
      ) : null}
      {video.coach_reply ? (
        <div className="note-read">
          <span className="from">{coachName ? `Coach ${coachName}` : 'Coach'}</span>
          <p>{video.coach_reply}</p>
        </div>
      ) : null}
    </div>
  );
}
